/** Shared mobile selector: teams are separate from the optional child view. */
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useParent } from '../../context/ParentContext';
import {
  availableTeams, accountTeamRoleLabel, contextKey,
  hasOwnTeamWorkspace, contextDisplayLabel,
} from '../../lib/accountContexts';
import { ChevronDown, Check, Users, User, Plus } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { getTeamRole, teamRoleLabelSv, isAppAdmin } from '../../lib/roles';

export default function MobileTeamHeader({ className, compact = false }) {
  const { user, teams, activeTeam, changeActiveTeam, teamSwitching } = useAuth();
  const {
    contexts, active, setActiveContext, clearActiveContext,
    loading, error, refresh, switching, isParentView, leaderWorkspace,
  } = useParent();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const sheetTitleRef = useRef(null);
  const visibleTeams = availableTeams(teams);
  const teamIds = new Set(visibleTeams.map(team => team.id));
  const hasOwnWorkspace = hasOwnTeamWorkspace(user, activeTeam)
    || visibleTeams.some(team => hasOwnTeamWorkspace(user, team));
  const hasParentContext = contexts.length > 0
    || getTeamRole(user, activeTeam?.id) === 'parent'
    || visibleTeams.some(team => getTeamRole(user, team.id) === 'parent');
  const parentOnly = !hasOwnWorkspace && hasParentContext;
  const showChildren = !leaderWorkspace && (hasParentContext || isParentView);
  const childView = isParentView || parentOnly;
  const selectorTitle = showChildren && hasOwnWorkspace ? 'Lag och barn' : (parentOnly ? 'Välj barn' : 'Välj lag');
  const ownTeams = leaderWorkspace ? visibleTeams : visibleTeams.filter(team => hasOwnTeamWorkspace(user, team));
  const disabled = busy || teamSwitching || switching;

  const triggerTitle = childView
    ? (active?.player_name || 'Välj barn')
    : (activeTeam?.name || 'Välj lag');
  const triggerSubtitle = childView
    ? (active?.team_name || 'Välj barn och lag')
    : contextDisplayLabel(null, activeTeam, user);

  const handleSelectTeam = async (team) => {
    if (disabled) return;
    setBusy(true);
    try {
      await changeActiveTeam(team.id);
      clearActiveContext();
      setIsOpen(false);
      navigate('/oversikt', { replace: true });
    } catch (err) {
      toast.error(err?.message || 'Kunde inte byta lag.');
    } finally { setBusy(false); }
  };

  const handleSelectChild = async (link) => {
    if (disabled) return;
    setBusy(true);
    try {
      await setActiveContext({ playerProfileId: link.player_profile_id, teamId: link.team_id });
      setIsOpen(false);
      navigate('/oversikt', { replace: true });
    } catch (err) {
      toast.error(err?.message || 'Kunde inte byta till barnvyn.');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={() => setIsOpen(true)} disabled={disabled}
        className={cn("w-full px-4 py-3 flex items-center justify-between rounded-2xl bg-[rgb(var(--tn-tint-ffffff))]/[0.025] backdrop-blur-sm shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)] active:bg-[rgb(var(--tn-tint-ffffff))]/[0.045] transition-colors disabled:opacity-50", compact && '!py-0 h-11 relative isolate !bg-transparent !backdrop-blur-none !shadow-none', className)}
        data-testid="mobile-team-switcher-trigger" aria-label={selectorTitle} aria-expanded={isOpen}>
        {compact && <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 inset-y-1 -z-10 rounded-full bg-[rgb(var(--tn-tint-ffffff))]/[0.025] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)]" />}
        <div className="flex items-center gap-2 min-w-0">
          {childView ? <User className="h-4 w-4 shrink-0 text-primary" /> : <Users className="h-4 w-4 shrink-0 text-[var(--t-text-secondary)]" />}
          <div className="min-w-0 text-left">
            <p className="truncate text-[13px] font-semibold text-[var(--t-text-primary)]" title={childView ? `${triggerTitle} · ${triggerSubtitle}` : triggerTitle}>
              {triggerTitle}
              {compact && childView && active?.team_name && <span className="font-normal text-[var(--t-text-secondary)]"> · {active.team_name}</span>}
            </p>
            {!compact && <p className="truncate text-[10px] text-[var(--t-text-secondary)]">{triggerSubtitle}</p>}
          </div>
        </div>
        <ChevronDown className="ml-1 h-4 w-4 shrink-0 text-[var(--t-text-secondary)]" />
      </button>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent onOpenAutoFocus={(event) => { event.preventDefault(); sheetTitleRef.current?.focus(); }} side="bottom" className="bg-[var(--t-bg1)] border-t border-[var(--border)] rounded-t-[28px] p-0 max-h-[80dvh] flex flex-col gap-0         [&>button]:h-11 [&>button]:w-11 [&>button]:rounded-xl [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:top-2 [&>button]:right-2 [&>button]:focus:ring-0 [&>button]:focus:ring-offset-0 [&>button]:focus-visible:ring-2 [&>button]:focus-visible:ring-primary" data-testid="mobile-team-switcher-sheet">
          <SheetHeader className="px-5 pt-5 pb-3 pr-16 shrink-0 text-left border-b border-[rgb(var(--tn-border-ffffff))]/[0.06]">
            <SheetTitle ref={sheetTitleRef} tabIndex={-1} className="text-lg font-semibold text-[var(--t-text-primary)] outline-none">{selectorTitle}</SheetTitle>
            <SheetDescription className="text-xs text-[var(--t-text-secondary)] truncate">{user?.name || user?.username || user?.email}{isAppAdmin(user) ? ' · Plattformsadmin' : ''}</SheetDescription>
            
          </SheetHeader>
          <div className="min-h-0 overflow-y-auto overscroll-contain px-4 pt-2 space-y-1.5 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            {!parentOnly && <p className="px-1 py-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--t-text-secondary)]">Mina lag</p>}
            {!parentOnly && ownTeams.map(team => (
              <button type="button" key={team.id} onClick={() => handleSelectTeam(team)} disabled={disabled}
                className={cn("w-full flex items-center gap-3 rounded-2xl border border-transparent px-3 py-2.5 text-left transition-colors active:bg-[rgb(var(--tn-tint-ffffff))]/[0.06] disabled:opacity-50", activeTeam?.id === team.id && !isParentView ? 'bg-primary/[0.08] border-primary/20' : "bg-[rgb(var(--tn-tint-ffffff))]/[0.025]")}
                data-testid={`mobile-team-item-${team.id}`} aria-pressed={activeTeam?.id === team.id && !isParentView}>
                <span className="h-10 w-10 rounded-xl bg-[rgb(var(--tn-tint-ffffff))]/[0.05] flex items-center justify-center shrink-0"><Users className="h-5 w-5 text-primary" /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold break-words leading-snug">{team.name}</p>
                  <p className="text-xs leading-relaxed text-[var(--t-text-secondary)] mt-0.5 break-words">{getTeamRole(user, team.id) ? teamRoleLabelSv(getTeamRole(user, team.id)) : accountTeamRoleLabel(user, team)}{team.organization_name ? ` · ${team.organization_name}` : ''}</p>
                </div>
                {activeTeam?.id === team.id && !isParentView && <Check className="h-4 w-4 text-primary" />}
              </button>
            ))}
            {!parentOnly && !ownTeams.length && <p className="px-3 py-2 text-xs text-muted-foreground">Inga lag att välja.</p>}

            <div className="flex flex-wrap gap-2 border-t border-border py-3">
              <button type="button" disabled={disabled} onClick={()=>{setIsOpen(false);navigate('/create-team');}} className="flex min-h-11 items-center gap-2 rounded-lg border border-border px-3 text-sm"><Plus className="h-4 w-4"/>Skapa nytt lag</button>
              <button type="button" disabled={disabled} onClick={()=>{setIsOpen(false);navigate('/join');}} className="min-h-11 rounded-lg border border-border px-3 text-sm">Anslut med lagkod</button>
            </div>
            {showChildren && <p className="px-1 py-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--t-text-secondary)]">Mina barn</p>}
            {showChildren && loading && <p className="px-3 text-xs text-muted-foreground">Laddar barn…</p>}
            {!leaderWorkspace && error && <button type="button" onClick={refresh} className="px-3 py-2 text-left text-xs text-muted-foreground">{error}</button>}
            {showChildren && !loading && !error && !contexts.length && <p className="px-3 py-2 text-xs text-muted-foreground">Inga barn att välja.</p>}
            {showChildren && contexts.map(link => {
              const key = contextKey(link.player_profile_id, link.team_id);
              const selected = active && key === contextKey(active.player_profile_id, active.team_id);
              const available = teamIds.has(link.team_id);
              return (
                <button type="button" key={key} disabled={disabled || loading || !available} onClick={() => handleSelectChild(link)} aria-pressed={Boolean(selected)}
                  className={cn('w-full flex items-center gap-3 rounded-2xl border border-transparent px-3 py-2.5 text-left disabled:opacity-50', selected ? 'bg-primary/[0.08] border-primary/20' : "bg-[rgb(var(--tn-tint-ffffff))]/[0.025]")}
                  data-testid={`mobile-context-item-${key}`}>
                  <User className="h-4 w-4 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{link.player_name || 'Barn'}</p><p className="truncate text-xs text-[var(--t-text-secondary)]">{link.team_name || 'Lag'}{!available && ' · Inte tillgängligt'}</p></div>
                  {selected && <Check className="h-4 w-4 text-primary" />}
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
